import { createAsyncThunk } from "@reduxjs/toolkit";

import { FeatureOption, mapFSMainFeatures, mapFSProjects, mapFSReportData } from "../Types/FinishingSchedule/FinishingSchedule";
import { mapFSAppliances } from "../Types/FinishingSchedule/Appliance";
import { mapFSBrands } from "../Types/FinishingSchedule/Brand";
import { mapFSColors } from "../Types/FinishingSchedule/Color";
import { mapFSDocuments } from "../Types/FinishingSchedule/Document";
import { mapFSItems } from "../Types/FinishingSchedule/Item";
import { mapFSLocations } from "../Types/FinishingSchedule/Location";
import { mapFSSizes } from "../Types/FinishingSchedule/Size";
import { mapFSTypes } from "../Types/FinishingSchedule/Type";
import { mapFSPaints } from "../Types/FinishingSchedule/Paint/Paint";
import { mapFSAccessories } from "../Types/FinishingSchedule/Accessory/Accessory";
import { mapFSAccessorySubs } from "../Types/FinishingSchedule/Accessory/AccessorySub";
import { mapFSFloorFinishing } from "../Types/FinishingSchedule/FloorFinishing/FloorFinishing";
import { mapFSTiles } from "../Types/FinishingSchedule/Tile/Tile";
import { mapFSTileShops } from "../Types/FinishingSchedule/Tile/TileShop";
import { mapFSDoors } from "../Types/FinishingSchedule/Door/Door";
import { mapFSDoorSizes } from "../Types/FinishingSchedule/Door/DoorSize";

import { getFSMainFeatureList, getFSProjectData, getFSReportData } from "../../Api/FinishingScheduleService/FinishingScheduleService";
import { getFSApplianceList } from "../../Api/FinishingScheduleService/ApplianceService";
import { getFSBrandList } from "../../Api/FinishingScheduleService/BrandService";
import { getFSColorList } from "../../Api/FinishingScheduleService/ColorService";
import { getFSDocumentList } from "../../Api/FinishingScheduleService/DocumentService";
import { getFSItemList } from "../../Api/FinishingScheduleService/ItemService";
import { getFSLocationList } from "../../Api/FinishingScheduleService/LocationService";
import { getFSSizeList } from "../../Api/FinishingScheduleService/SizeService";
import { getFSTypeList } from "../../Api/FinishingScheduleService/TypeService";
import { getFSPaintList } from "../../Api/FinishingScheduleService/PaintService/PaintService";
import { getFSAccessoryList } from "../../Api/FinishingScheduleService/PaintService/AccessoryService";
import { getFSAccessorySubList } from "../../Api/FinishingScheduleService/PaintService/AccessorySubService";
import { getFSTileList } from "../../Api/FinishingScheduleService/TilesService/TilesService";
import { getFSTileShopList } from "../../Api/FinishingScheduleService/TilesService/TileShop";
import { getFSFloorFinishingList } from "../../Api/FinishingScheduleService/FloorFinishingService/FloorFinishingService";
import { getFSDoorList, getFSDoorSizeList } from "../../Api/FinishingScheduleService/DoorService/DoorService";
import { getFSBathroomFeatureList, getFSBathroomList } from "../../Api/FinishingScheduleService/BathroomService/BathroomService";
import { getFSLightList } from "../../Api/FinishingScheduleService/LightService/LightService";
import { mapFSBathrooms } from "../Types/FinishingSchedule/Bathroom/Bathroom";
import { mapFSBathroomFeatures } from "../Types/FinishingSchedule/Bathroom/BathroomFeature";
import { getFSKitchenCollectionList, getFSKitchenFinishList, getFSKitchenList } from "../../Api/FinishingScheduleService/KitchenService/KitchenService";
import { mapFSKitchens } from "../Types/FinishingSchedule/Kitchen/Kitchen";
import { mapFSKitchenCollections } from "../Types/FinishingSchedule/Kitchen/KitchenCollection";
import { mapFSKitchenFinishes } from "../Types/FinishingSchedule/Kitchen/KitchenFinish";
import { mapFSKitchenRanges } from "../Types/FinishingSchedule/Kitchen/KitchenRange";
import { getFSKitchenRangeList } from "../../Api/FinishingScheduleService/KitchenService/KitchenRangeService";
import { mapFSLights } from "../Types/FinishingSchedule/Light/Light";
import { getFSOthersList } from "../../Api/FinishingScheduleService/OthersService/OthersService";
import { mapFSOthers } from "../Types/FinishingSchedule/Other/Other";
import { getFSKitchenStyleList } from "../../Api/FinishingScheduleService/KitchenService/KitchenStyleService";
import { mapFSKitchenStyles } from "../Types/FinishingSchedule/Kitchen/KitchenStyle";


// fetch finishing schedule projects
export const fetchFSProjectList = createAsyncThunk(
    'fetchFSProjectList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSProjectData();
            if (response.status) {
                return mapFSProjects(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch finishing schedule projects data');
        }
    }
);

// fetch finishing schedule main features
export const fetchFSMainFeatureList = createAsyncThunk(
    'fetchFSMainFeatureList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSMainFeatureList();
            if (response.status) {
                return mapFSMainFeatures(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch finishing schedule features data');
        }
    }
);

// fetch appliance
export const fetchFSApplianceList = createAsyncThunk(
    'fetchFSApplianceList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSApplianceList();
            if (response.status) {
                return mapFSAppliances(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch appliance data');
        }
    }
);

// fetch brand
export interface FSBrandListArgs {
    category: 'Kitchen' | 'Paint' | 'Bathroom' | 'Floor Finishing' | 'Others';
    sub_category?:
    | 'Type'
    | 'Base Units'
    | 'Wall Units'
    | 'Worktop'
    | 'Door'
    | 'Handle'
    | 'Appliance'
    | 'Paint'
    | null;
}

export const fetchFSBrandList = createAsyncThunk(
    'fetchFSBrandList',
    async ({ category, sub_category }: FSBrandListArgs, { rejectWithValue }) => {
        try {
            const response = await getFSBrandList(category, sub_category!);
            if (response.status) {
                return mapFSBrands(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch brand data');
        }
    }
);

// fetch color
export interface FSColorListArgs {
    category: 'Kitchen' | 'Paint' | 'Tiles' | 'Door' | 'Bathroom';
    sub_category?:
    | 'Type'
    | 'Base Units'
    | 'Wall Units'
    | 'Worktop'
    | 'Door'
    | 'Handle'
    | 'Appliance'
    | 'Paint'
    | null;
}

export const fetchFSColorList = createAsyncThunk(
    'fetchFSColorList',
    async ({ category, sub_category }: FSColorListArgs, { rejectWithValue }) => {
        try {
            const response = await getFSColorList(category, sub_category!);
            if (response.status) {
                return mapFSColors(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch color data');
        }
    }
);

// fetch document
export const fetchFSDocumentList = createAsyncThunk(
    'fetchFSDocumentList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSDocumentList(fs_feature_id);
            if (response.status) {
                return mapFSDocuments(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch document data');
        }
    }
);

// fetch item
export interface FSItemArgs {
    category: 'Light' | 'Bathroom';
}

export const fetchFSItemList = createAsyncThunk(
    'fetchFSItemList',
    async ({ category }: FSItemArgs, { rejectWithValue }) => {
        try {
            const response = await getFSItemList(category);
            if (response.status) {
                return mapFSItems(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch item data');
        }
    }
);

// fetch location
export interface FSLocationListArgs {
    category: 'Light' | 'Paint' | 'Bathroom' | 'Floor Finishing' | 'Others';
    sub_category?: 'Light' | 'Paint' | 'Bathroom' | 'Floor Finishing' | 'Others' | null;
}

export const fetchFSLocationList = createAsyncThunk(
    'fetchFSLocationList',
    async ({ category, sub_category }: FSLocationListArgs, { rejectWithValue }) => {
        try {
            const response = await getFSLocationList(category, sub_category!);
            if (response.status) {
                return mapFSLocations(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch location data');
        }
    }
);

// fetch size
export const fetchFSSizeList = createAsyncThunk(
    'fetchFSSizeList',
    async (category: 'Paint' | 'Floor Finishing', { rejectWithValue }) => {
        try {
            const response = await getFSSizeList(category);
            if (response.status) {
                return mapFSSizes(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch size data');
        }
    }
);

// fetch type
export interface FSTypeListArgs {
    category: 'Tiles' | 'Light' | 'Floor Finishing' | 'Kitchen';
    sub_category?:
    | 'Type'
    | 'Base Units'
    | 'Wall Units'
    | 'Worktop'
    | 'Door'
    | 'Handle'
    | 'Appliance'
    | 'Paint'
    | null;
}

export const fetchFSTypeList = createAsyncThunk(
    'fetchFSTypeList',
    async ({ category, sub_category }: FSTypeListArgs, { rejectWithValue }) => {
        try {
            const response = await getFSTypeList(category, sub_category!);
            if (response.status) {
                return mapFSTypes(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch type data');
        }
    }
);

// fetch paint
export const fetchFSPaintList = createAsyncThunk(
    'fetchFSPaintList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSPaintList(fs_feature_id);
            if (response.status) {
                return mapFSPaints(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch paint data');
        }
    }
);

// fetch accessory name
export interface FSAccessoryListArgs {
    category: 'Paint' | 'Tiles' | 'Door' | 'Floor Finishing';
}

export const fetchFSAccessoryList = createAsyncThunk(
    'fetchFSAccessoryList',
    async ({ category }: FSAccessoryListArgs, { rejectWithValue }) => {
        try {
            const response = await getFSAccessoryList(category);
            if (response.status) {
                return mapFSAccessories(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch accessory data');
        }
    }
);

// fetch accessory sub name
export const fetchFSAccessorySubList = createAsyncThunk(
    'fetchFSAccessorySubList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSAccessorySubList();
            if (response.status) {
                return mapFSAccessorySubs(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch sub accessory data');
        }
    }
);

// fetch floor finishing
export const fetchFSFloorFinishingList = createAsyncThunk(
    'fetchFSFloorFinishingList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSFloorFinishingList(fs_feature_id);
            if (response.status) {
                return mapFSFloorFinishing(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch floor finishing data');
        }
    }
);

// fetch tiles
export const fetchFSTileList = createAsyncThunk(
    'fetchFSTileList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSTileList(fs_feature_id);
            if (response.status) {
                return mapFSTiles(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch tiles data');
        }
    }
);

// fetch tile shop
export const fetchFSTileShopList = createAsyncThunk(
    'fetchFSTileShopList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSTileShopList();
            if (response.status) {
                return mapFSTileShops(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch tile shop data');
        }
    }
);

// fetch door
export const fetchFSDoorList = createAsyncThunk(
    'fetchFSDoorList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSDoorList(fs_feature_id);
            if (response.status) {
                return mapFSDoors(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch door data');
        }
    }
);

// fetch door size
export const fetchFSDoorSizeList = createAsyncThunk(
    'fetchFSDoorSizeList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSDoorSizeList();
            if (response.status) {
                return mapFSDoorSizes(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch door size data');
        }
    }
);

// fetch bathroom
export const fetchFSBathroomList = createAsyncThunk(
    'fetchFSBathroomList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSBathroomList(fs_feature_id);
            if (response.status) {
                return mapFSBathrooms(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch bathroom data');
        }
    }
);

// fetch bathroom feature
export const fetchFSBathroomFeatureList = createAsyncThunk(
    'fetchFSBathroomFeatureList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSBathroomFeatureList();
            if (response.status) {
                return mapFSBathroomFeatures(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch bathroom feature data');
        }
    }
);

// fetch kitchen
export const fetchFSKitchenList = createAsyncThunk(
    'fetchFSKitchenList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSKitchenList(fs_feature_id);
            if (response.status) {
                return mapFSKitchens(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch kitchen data');
        }
    }
);

// fetch kitchen collection
export const fetchFSKitchenCollectionList = createAsyncThunk(
    'fetchFSKitchenCollectionList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSKitchenCollectionList();
            if (response.status) {
                return mapFSKitchenCollections(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch kitchen collection data');
        }
    }
);

// fetch kitchen finish
export const fetchFSKitchenFinishList = createAsyncThunk(
    'fetchFSKitchenFinishList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSKitchenFinishList();
            if (response.status) {
                return mapFSKitchenFinishes(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch kitchen finish data');
        }
    }
);

// fetch kitchen range
export const fetchFSKitchenRangeList = createAsyncThunk(
    'fetchFSKitchenRangeList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSKitchenRangeList();
            if (response.status) {
                return mapFSKitchenRanges(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch kitchen range data');
        }
    }
);

// fetch kitchen style
export const fetchFSKitchenStyleList = createAsyncThunk(
    'fetchFSKitchenStyleList',
    async (_, { rejectWithValue }) => {
        try {
            const response = await getFSKitchenStyleList();
            if (response.status) {
                return mapFSKitchenStyles(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch kitchen style data');
        }
    }
);

// fetch light
export const fetchFSLightList = createAsyncThunk(
    'fetchFSLightList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSLightList(fs_feature_id);
            if (response.status) {
                return mapFSLights(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch light data');
        }
    }
);

// fetch other
export const fetchFSOtherList = createAsyncThunk(
    'fetchFSOtherList',
    async (fs_feature_id: number, { rejectWithValue }) => {
        try {
            const response = await getFSOthersList(fs_feature_id);
            if (response.status) {
                return mapFSOthers(response.body);
            } else {
                return rejectWithValue(response.error);
            }
        } catch (error: any) {
            return rejectWithValue('Failed to fetch others data');
        }
    }
);

// fetch fs report data
export const fetchFSReportData = createAsyncThunk(
    "finishingSchedule/fetchFSReportData",
    async (
        {
            generateReport,
            featureOptions,
        }: { generateReport: 1 | 0; featureOptions: FeatureOption[] },
        { rejectWithValue }
    ) => {
        try {
            const response = await getFSReportData(generateReport, featureOptions);

            if (response.status && response.data) {
                return mapFSReportData(response.data);
            } else {
                return rejectWithValue(response.error || response.massage || "Unknown error");
            }
        } catch (error: any) {
            return rejectWithValue("Failed to fetch report data");
        }
    }
);

